import { ApiDate, ApiDateTime } from 'app/shared/models/essette-api';
import { DateValue, DateTimeValue } from 'app/shared/lib/date-time';

/**
 * @module app.shared
 */

/**
 * @note Use this function for all no ops. (Matt)
 */
export function noop() { }

/**
 * @note Use this function to handle an event and stop its propagation. (Matt)
 */
export function stop(e: Event) { e.stopPropagation(); }

/**
 * @note Use this function for predicates/filters that should always evaluate to true. (mwj)
 */
export function alwaysTrue(): true { return true; }

/**
 * @note Use this function for predicates/filters that should always evaluate to true. (mwj)
 */
export function alwaysFalse(): false { return false; }

/**
 * @note use this as a mapping function when an item should map to itself, or for a no-op clone. (mwj)
 */
export function mapIdentity<T>(value: T): T { return value; }

/**
 * @description Maps any value to itself.
 */
export function map(val: any): any { return val; }

/**
 * Maps the properties of an object according to the passed function.
 * @param obj - The object whose properties are mapped.
 * @param transform - The mapping function.
 * @example mapProperties({ a: 1, b: 2 }, v => v*2) // { a: 2, b: 4 }
 */
export function mapProperties<T = any, U = any>(obj: Record<string, T>, transform: (val: T) => U) {
    return Object.keys(obj).reduce((mapped, key) => Object.assign(mapped, { [key]: transform(obj[key]) }), {});
}

/**
 * @description Clamps a value between a min and max value.
 */
export function clamp(val: number, min: number, max: number): number { return Math.min(Math.max(val, min), max); }

/**
 * @description Maps any value to its truthiness.
 */
export function truthy(val: any): boolean { return !!val; }

/**
 * @description Maps any value to its falsiness.
 */
export function falsy(val: any): boolean { return !val; }

/**
 * @description Tests strict equality.
 */
export function equals(a: any, b: any): boolean { return a === b; }

/**
 * @description Tests if b is at least a, i.e. a >= b.
 */
export function min(a: number, b: number): boolean { return a >= b; }

/**
 * @description Tests if b is at most a, i.e. a <= b;
 */
export function max(a: number, b: number): boolean { return a <= b; }

/** Tests if date a is before date b */
export function before(a: ApiDate | ApiDateTime, b: ApiDate | ApiDateTime): boolean {
    if(a === null || a === undefined || b === null || b === undefined) { return false; }

    // date
    if(a.length === 10 && b.length === 10) {
        const aDate = new DateValue(a);
        const bDate = new DateValue(b);
        if(!aDate.isValid() || !bDate.isValid()) { return false; }
        return aDate.isEqual(bDate) || aDate.isBefore(bDate);
    }
    // date/time
    const aDateTime = new DateTimeValue(a);
    const bDateTime = new DateTimeValue(b);
    if(!aDateTime.isValid() || !bDateTime.isValid()) { return false; }
    return aDateTime.isBefore(bDateTime);
}

/** Tests if date a is after date b */
export function after(a: ApiDate | ApiDateTime, b: ApiDate | ApiDateTime): boolean {
    if(a === null || a === undefined || b === null || b === undefined) { return false; }

    // date
    if(a.length === 10 && b.length === 10) {
        const aDate = new DateValue(a);
        const bDate = new DateValue(b);
        if(!aDate.isValid() || !bDate.isValid()) { return false; }
        return aDate.isEqual(bDate) || aDate.isAfter(bDate);
    }
    // date/time
    const aDateTime = new DateTimeValue(a);
    const bDateTime = new DateTimeValue(b);
    if(!aDateTime.isValid() || !bDateTime.isValid()) { return false; }
    return aDateTime.isAfter(bDateTime);
}
